/**
 * [sc-48139] Le regole delle fatture elettroniche che la pagina può sapere
 * senza chiedere all'API: etichette degli stati, cosa si può fare in ciascuno,
 * scadenze. Funzioni pure. Gemello di `crmstart-api/src/config/fatture.js`.
 */

export const ETICHETTE_STATO = Object.freeze({
  in_invio: "In invio",
  da_verificare: "Da verificare",
  inviata: "Inviata",
  consegnata: "Consegnata",
  in_cassetto: "Nel cassetto fiscale",
  scartata: "Scartata",
});

const MESSAGGI_ESITO = Object.freeze({
  da_verificare: "DocuMI non ha risposto. Non reinviarla: la verifichiamo noi.",
});

export function messaggioEsito(status) {
  return MESSAGGI_ESITO[status] ?? null;
}

/** Si emette se non c'è ancora una fattura, o se l'ultima è stata scartata. */
export function puoEmettere(fattura) {
  return !fattura || fattura.status === "scartata";
}

/** [sc-49222] Solo consegnata e in cassetto hanno PDF, XML e ricevute. */
export function haDocumenti(fattura) {
  return ["consegnata", "in_cassetto"].includes(fattura?.status);
}

const DUE_MINUTI = 2 * 60 * 1000;

/**
 * Si verifica una fattura `da_verificare` o `inviata` (l'esito dello SdI si
 * può chiedere a mano, senza aspettare lo script), o una `in_invio` ferma da
 * più di due minuti. L'età si conta da `statusAt` (quando è entrata nello
 * stato), come fa l'API; `updatedAt` solo se `statusAt` manca.
 */
export function puoVerificare(fattura, now = Date.now()) {
  if (!fattura) return false;
  if (fattura.status === "da_verificare" || fattura.status === "inviata")
    return true;
  if (fattura.status !== "in_invio") return false;
  const da = Date.parse(fattura.statusAt ?? fattura.updatedAt);
  return Number.isFinite(da) && now - da > DUE_MINUTI;
}

/** Data documento + giorni, in UTC: l'ora legale non sposta il giorno. */
export function scadenzaDa(dataDocumento, giorni) {
  const [y, m, d] = String(dataDocumento).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + giorni)).toISOString().slice(0, 10);
}

export const MODALITA = Object.freeze([
  { value: "bonifico", label: "Bonifico" },
  { value: "contanti", label: "Contanti" },
  { value: "assegno", label: "Assegno" },
  { value: "carta", label: "Carta" },
  { value: "riba", label: "RiBa" },
]);

const METODI_ORDINE = Object.freeze({
  Contanti: "contanti",
  Bonifico: "bonifico",
  "Carta di credito": "carta",
});

export function modalitaDaMetodo(metodo) {
  return METODI_ORDINE[metodo] ?? null;
}

const ETICHETTE_INDIRIZZO = Object.freeze([
  "indirizzo del cliente",
  "CAP del cliente",
  "comune del cliente",
  // Stessa frase di MANCANZA_CLIENTE_ESTERO dell'API (Ruling 30).
  "cliente con sede in Italia (i clienti esteri non sono ancora fatturabili)",
]);

/**
 * Le mancanze dell'indirizzo del cliente, con le stesse etichette e regole di
 * `mancanzeCliente` dell'API: la modale le ricalcola mentre si scrive.
 */
export function mancanzeIndirizzo(indirizzo = {}) {
  const vuoto = (v) => !String(v ?? "").trim();
  const m = [];
  const paese = String(indirizzo.country ?? "").trim() || "IT";
  if (paese.toUpperCase() !== "IT") m.push(ETICHETTE_INDIRIZZO[3]);
  if (vuoto(indirizzo.street)) m.push(ETICHETTE_INDIRIZZO[0]);
  if (
    paese.toUpperCase() === "IT" &&
    !/^\d{5}$/.test(String(indirizzo.zip ?? "").trim())
  )
    m.push(ETICHETTE_INDIRIZZO[1]);
  if (vuoto(indirizzo.city)) m.push(ETICHETTE_INDIRIZZO[2]);
  return m;
}

/** Le mancanze dell'anteprima senza quelle d'indirizzo, che vanno ricalcolate. */
export function senzaMancanzeIndirizzo(mancanze = []) {
  return mancanze.filter((x) => !ETICHETTE_INDIRIZZO.includes(x));
}

/** [sc-49176] Etichetta della mancanza, identica a quella dell'API. */
export const ETICHETTA_INTENTO = "dichiarazione d'intento del cliente";
const RE_PROTOCOLLO_INTENTO = /^\d{17}-\d{6}$/;

export function protocolloIntentoValido(protocollo) {
  return RE_PROTOCOLLO_INTENTO.test(String(protocollo ?? "").trim());
}

/** Data ISO YYYY-MM-DD esistente nel calendario. */
export function dataIsoValida(data) {
  const v = String(data ?? "");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}

/**
 * La dichiarazione d'intento è completa e nel formato che l'API accetta. Con
 * `oggi` la data non può venire dopo: la dichiarazione precede la fattura.
 */
export function intentoValido(intento = {}, oggi = null) {
  return (
    protocolloIntentoValido(intento.protocollo) &&
    dataIsoValida(intento.data) &&
    (!oggi || intento.data <= oggi)
  );
}
